import { useCallback, useState } from 'react';
import { useAtom } from 'jotai';
import { ApiError, useCallApi } from './common';
import { UserApi, LoginResponse, PatchUserRequest, PatchUserResponse, ExternalApi, UserActiveCoursesResponse, SiteType } from '../../api';
import { userAtom, activeCoursesAtom } from '../../atom/Atom';

export const useUser = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { callApi, defaultConfiguration } = useCallApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useAtom(userAtom);
  const [activeCourses, setActiveCourses] = useAtom(activeCoursesAtom);

  const getUserInfo: () => Promise<void> = useCallback(async () => {
    setLoading(true);
    setError(null);
    const api = new UserApi(await defaultConfiguration());
    const response = await callApi<LoginResponse, null>(null, () => api.apiV1UserGet());

    if (response.success) {
      const userData = response.success.data?.user;
      setUser(userData);
    } else {
      setError(response.error);
    }
    setLoading(false);
  }, [callApi, defaultConfiguration, setUser]);

  const patchUser = useCallback(async (birthdate: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    const api = new UserApi(await defaultConfiguration());
    const response = await callApi<PatchUserResponse, PatchUserRequest>({ birthdate }, (req) => api.apiV1UserPatch(req));

    try {
      if (response.success) {
        setUser((prev) => prev ? { ...prev, birthdate } : prev);
        return true;
      } else {
        setError(response.error);
        return false;
      }
    } finally {
      setLoading(false);
    }
  }, [callApi, defaultConfiguration, setUser]);

  const getActiveCourses: () => Promise<void> = useCallback(async () => {
    let codes: string[] = [];
    if (!user || user.siteName !== SiteType.License) {
      setActiveCourses(codes);
      return;
    }

    setLoading(true);
    setError(null);
    const api = new ExternalApi(await defaultConfiguration());
    const response = await callApi<UserActiveCoursesResponse, null>(null, () => api.apiV1ExternalLicenseUserActiveCoursesGet());
    if (response.success) {
      codes = response.success.data?.codes || [];
    } else {
      setError(response.error);
    }
    setActiveCourses(codes);
    setLoading(false);
  }, [callApi, defaultConfiguration, user, setActiveCourses]);

  return {
    error,
    loading,
    user,
    getUserInfo,
    patchUser,
    activeCourses,
    getActiveCourses,
  };
};
